import { GET, POST, PUT, DELETE } from './executer';
import apiServiceInstance from './apiService';

/**
 * Supported job types
 */
export type JobType =
  | 'backup'
  | 'daily_forecast'
  | 'hourly_forecast'
  | 'minutely_forecast'
  | 'weather_message'
  | 'stations_status_check'
  | 'radar_status_check'
  | 'archive_observations'
  | 'test';

/**
 * Job status lifecycle
 */
export type JobStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed';

/**
 * Event Job entity
 */
export interface EventJob {
  id: string;
  season_year: number;
  circuit_id: string;
  start_date: string;
  job_type: JobType;
  scheduled_at: string;
  status: JobStatus;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  job_config: Record<string, unknown> | null;
  created_at: string;
  created_by: string;
  updated_at: string;
}

/**
 * Request body for creating a job
 */
export interface CreateEventJobRequest {
  job_type: JobType;
  scheduled_at: string;
  job_config?: Record<string, unknown>;
}

/**
 * Request body for updating a job
 */
export interface UpdateEventJobRequest {
  scheduled_at?: string;
  status?: JobStatus;
  job_config?: Record<string, unknown>;
}

/**
 * Valid job types array for validation
 */
export const VALID_JOB_TYPES: JobType[] = [
  'backup',
  'daily_forecast',
  'hourly_forecast',
  'minutely_forecast',
  'weather_message',
  'stations_status_check',
  'radar_status_check',
  'archive_observations',
  'test',
];

/**
 * Valid job statuses array for validation
 */
export const VALID_JOB_STATUSES: JobStatus[] = [
  'pending',
  'running',
  'succeeded',
  'failed',
];

/**
 * Job type display configuration
 */
export const JOB_TYPE_CONFIG: Record<JobType, { label: string; color: string; icon: string; description: string }> = {
  backup: { 
    label: 'Backup', 
    color: '#722ed1', 
    icon: '💾',
    description: 'Daily BigQuery table backup'
  },
  daily_forecast: { 
    label: 'Daily Forecast', 
    color: '#1890ff', 
    icon: '📅',
    description: 'Daily forecast generation (pre-event)'
  },
  hourly_forecast: { 
    label: 'Hourly Forecast', 
    color: '#13c2c2', 
    icon: '⏰',
    description: 'Hourly forecast generation (during event)'
  },
  minutely_forecast: {
    label: 'Minutely Forecast',
    color: '#08979c',
    icon: '⏱️',
    description: '30-minute minutely forecast generation around sessions'
  },
  weather_message: { 
    label: 'Weather Message', 
    color: '#fa8c16', 
    icon: '📨',
    description: 'FIA weather message alerts'
  },
  stations_status_check: { 
    label: 'Stations Check', 
    color: '#52c41a', 
    icon: '📡',
    description: 'Stations pipeline status check'
  },
  radar_status_check: { 
    label: 'Radar Check', 
    color: '#2f54eb', 
    icon: '📡',
    description: 'Radar pipeline status check'
  },
  archive_observations: {
    label: 'Archive Observations',
    color: '#531dab',
    icon: '📦',
    description: 'Archive observations to GCS after event ends'
  },
  test: { 
    label: 'Test', 
    color: '#eb2f96', 
    icon: '🧪',
    description: 'Test job for debugging the pipeline'
  },
};

/**
 * Job status display configuration
 */
export const JOB_STATUS_CONFIG: Record<JobStatus, { label: string; color: string; icon: string }> = {
  pending: { label: 'Pending', color: '#faad14', icon: '⏳' },
  running: { label: 'Running', color: '#1890ff', icon: '🔄' },
  succeeded: { label: 'Succeeded', color: '#52c41a', icon: '✅' },
  failed: { label: 'Failed', color: '#f5222d', icon: '❌' },
};

/**
 * Weather message type labels
 */
export const WEATHER_MESSAGE_TYPE_CONFIG: Record<string, { label: string; icon: string }> = {
  air_temperature: { label: 'Air Temperature', icon: '🌡️' },
  rain_probability: { label: 'Rain Probability', icon: '🌧️' },
};

/**
 * A single log entry from Cloud Run job execution
 */
export interface JobLogEntry {
  timestamp: string;
  severity: string;
  message: string;
}

/**
 * Service for managing event jobs
 */
class EventJobsService {
  /**
   * Get the source query string based on isDemo flag
   */
  private getSourceParam(isDemo: boolean): string {
    return isDemo ? 'source=demo' : 'source=prod';
  }

  /**
   * Fetch jobs for a specific event
   * @param isDemo - Whether this is a demo event
   * @param statusFilter - Optional status filter (comma-separated)
   * @param jobTypeFilter - Optional job type filter (comma-separated)
   */
  async getEventJobs(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    isDemo: boolean = false,
    statusFilter?: string,
    jobTypeFilter?: string
  ) {
    const params = new URLSearchParams();
    params.append('source', isDemo ? 'demo' : 'prod');
    if (statusFilter) params.append('status', statusFilter);
    if (jobTypeFilter) params.append('job_type', jobTypeFilter);
    
    const url = `/api/jobs/${seasonYear}/${circuitId}/${startDate}?${params.toString()}`;
    
    return await apiServiceInstance.client.execute<EventJob[]>(
      GET,
      url
    );
  }

  /**
   * Fetch a single job by ID
   * @param isDemo - Whether to look in demo_event_jobs table
   */
  async getJobById(id: string, isDemo: boolean = false) {
    const url = `/api/jobs/id/${id}?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<EventJob>(
      GET,
      url
    );
  }

  /**
   * Create a new job
   * @param isDemo - Whether to insert into demo_event_jobs table
   */
  async createJob(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    job: CreateEventJobRequest,
    isDemo: boolean = false
  ) {
    const url = `/api/jobs/${seasonYear}/${circuitId}/${startDate}?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<EventJob>(
      POST,
      url,
      job
    );
  }

  /**
   * Update an existing job
   * @param isDemo - Whether to update in demo_event_jobs table
   */
  async updateJob(id: string, updates: UpdateEventJobRequest, isDemo: boolean = false) {
    const url = `/api/jobs/id/${id}?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<EventJob>(
      PUT,
      url,
      updates
    );
  }

  /**
   * Create multiple jobs in a batch operation
   * @param isDemo - Whether to insert into demo_event_jobs table
   */
  async createJobsBatch(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    jobs: CreateEventJobRequest[],
    isDemo: boolean = false
  ) {
    const url = `/api/jobs/${seasonYear}/${circuitId}/${startDate}/batch?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<{
      data: EventJob[];
      created: number;
      skipped: number;
      invalidJobs?: Array<{ index: number; reason: string }>;
    }>(
      POST,
      url,
      { jobs }
    );
  }

  /**
   * Cancel a job (delete)
   * @param isDemo - Whether to delete from demo_event_jobs table
   */
  async cancelJob(id: string, isDemo: boolean = false) {
    const url = `/api/jobs/id/${id}?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<{ message: string }>(
      DELETE,
      url
    );
  }

  /**
   * Delete multiple jobs in a batch operation
   * Uses POST instead of DELETE to support request body
   * @param isDemo - Whether to delete from demo_event_jobs table
   */
  async deleteJobsBatch(ids: string[], isDemo: boolean = false) {
    const url = `/api/jobs/batch/delete?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<{
      message: string;
      deleted: number;
    }>(
      POST,
      url,
      { ids }
    );
  }

  /**
   * Trigger a job manually
   * @param isDemo - Whether this is a demo event
   */
  async triggerJob(id: string, isDemo: boolean = false) {
    const url = `/api/jobs/id/${id}/trigger?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<EventJob>(
      POST,
      url,
      {}
    );
  }

  /**
   * Retry a failed job
   * @param scheduleNow - If true, reschedule to current time
   * @param isDemo - Whether this is a demo event
   */
  async retryJob(id: string, scheduleNow: boolean = false, isDemo: boolean = false) {
    const url = `/api/jobs/id/${id}/retry?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<EventJob>(
      POST,
      url,
      { schedule_now: scheduleNow }
    );
  }

  /**
   * Fetch Cloud Run execution logs for a job
   * @param isDemo - Whether this is a demo event
   */
  async getJobLogs(id: string, isDemo: boolean = false) {
    const url = `/api/jobs/id/${id}/logs?${this.getSourceParam(isDemo)}`;

    return await apiServiceInstance.client.execute<JobLogEntry[]>(
      GET,
      url
    );
  }

  /**
   * Format timestamp for display in the circuit's local timezone
   */
  formatTimestamp(timestamp: string | null, timeZone?: string): string {
    if (!timestamp) return '-';
    
    const date = new Date(timestamp);
    
    if (!timeZone) {
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    }
    
    return date.toLocaleString('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  /**
   * Get job type label
   */
  getJobTypeLabel(jobType: JobType): string {
    return JOB_TYPE_CONFIG[jobType]?.label || jobType;
  }

  /**
   * Get job status label
   */
  getJobStatusLabel(status: JobStatus): string {
    return JOB_STATUS_CONFIG[status]?.label || status;
  }

  /**
   * Get a formatted description of the job config
   */
  getJobConfigDescription(jobType: JobType, config: Record<string, unknown> | null): string {
    if (!config) return '';
    
    switch (jobType) {
      case 'weather_message': {
        const session = config.session_code as string;
        const msgType = config.message_type as string;
        const typeLabel = WEATHER_MESSAGE_TYPE_CONFIG[msgType]?.label || msgType;
        return `${session} - ${typeLabel}`;
      }
      case 'daily_forecast':
      case 'hourly_forecast':
      case 'minutely_forecast': {
        const refDate = config.reference_date as string;
        return refDate ? `Ref: ${refDate}` : '';
      }
      case 'backup': {
        return config.dry_run ? 'Dry Run' : '';
      }
      case 'test': {
        const parts: string[] = [];
        if (config.delay_seconds) parts.push(`${config.delay_seconds}s delay`);
        if (config.should_fail) parts.push('Will Fail');
        return parts.join(', ');
      }
      default:
        return '';
    }
  }
}

const eventJobsServiceInstance = new EventJobsService();
export default eventJobsServiceInstance;

